import { API_CONFIG } from '@/config/api';

const TOKEN_KEY = 'mediahub_api_token';

const withTimeout = async (url, options = {}, timeoutMs = API_CONFIG.TIMEOUT) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL.replace(/\/$/, '');
  }

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token) {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }

  async request(endpoint, options = {}, timeoutMs = API_CONFIG.TIMEOUT) {
    const headers = new Headers(options.headers || {});
    const token = this.getToken();

    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (!(options.body instanceof FormData) && options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }

    const response = await withTimeout(`${this.baseURL}${endpoint}`, {
      ...options,
      headers
    }, timeoutMs);

    let payload = null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) payload = await response.json();
    else payload = await response.text();

    if (!response.ok) {
      const message = payload?.error || payload?.message || `HTTP ${response.status}`;
      const error = new Error(message);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  }

  async health() {
    return this.request('/health');
  }

  async login(email, password) {
    const payload = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    this.setToken(payload.token);
    return payload;
  }

  async register(name, email, password) {
    const payload = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password })
    });
    this.setToken(payload.token);
    return payload;
  }

  async verify() {
    return this.request('/api/auth/verify');
  }

  async logout() {
    try {
      if (this.getToken()) await this.request('/api/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  async getProfile() {
    return this.request('/api/user/profile');
  }

  async getOrganizations() {
    return this.request('/api/organizations');
  }

  async getOrganizationUsage(organizationId) {
    return this.request(`/api/organizations/${organizationId}/usage`);
  }

  async getMedia(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    });
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/media${suffix}`);
  }

  async uploadMedia(file, onProgress) {
    const form = new FormData();
    form.append('file', file);

    // fetch não expõe progresso de upload de forma portátil; mantemos callbacks
    // compatíveis com a interface atual.
    if (onProgress) onProgress(5);
    const payload = await this.request('/api/media/upload', {
      method: 'POST',
      body: form
    });
    if (onProgress) onProgress(100);
    return payload;
  }

  async getStudioCapabilities() {
    return this.request('/api/studio/capabilities');
  }

  async createStudioReel(mediaId) {
    return this.request('/api/studio/reel', {
      method: 'POST',
      body: JSON.stringify({ mediaId })
    }, 190000);
  }

  async getMediaById(id) {
    return this.request(`/api/media/${id}`);
  }

  async updateMedia(id, data) {
    return this.request(`/api/media/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteMedia(id) {
    return this.request(`/api/media/${id}`, { method: 'DELETE' });
  }

  async shareMedia(id, data = {}) {
    return this.request(`/api/media/${id}/share`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getProtectedMediaUrl(id, kind = 'preview') {
    const headers = new Headers();
    const token = this.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await withTimeout(
      `${this.baseURL}/api/media/${id}/${kind}`,
      { headers }
    );

    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const payload = await response.json();
        message = payload?.error || payload?.message || message;
      } catch {
        // Corpo não JSON: preserva a mensagem HTTP.
      }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
}

export const apiService = new ApiService();
export { TOKEN_KEY };
